import { useState, type ReactNode } from "react";
import { solesCent, solesDm } from "../lib/money";
import { totalLineaCent, type CarritoItem, type CarritoTotales } from "../lib/useCarrito";

type Props = {
  items: CarritoItem[];
  totales: CarritoTotales;
  onSetCantidad: (presentacionId: string, cantidad: number) => void;
  onQuitar: (presentacionId: string) => void;
  onCobrar: () => void;
  onLimpiar: () => void;
  cobrando: boolean;
  /** A4: la tarjeta de venta cruzada. Va DENTRO del carrito, pegada al total (ver TarjetaSugerencia). */
  sugerencia?: ReactNode;
};

export function Carrito({ items, totales, onSetCantidad, onQuitar, onCobrar, onLimpiar, cobrando, sugerencia }: Props) {
  // «Vaciar» borraba toda la venta al primer toque: con 2 productos o más, antes pregunta.
  const [confirmarVaciar, setConfirmarVaciar] = useState(false);
  const unidades = items.reduce((n, it) => n + it.cantidad, 0);
  const vaciar = () => {
    if (items.length >= 2) setConfirmarVaciar(true);
    else onLimpiar();
  };

  return (
    <section className="flex flex-col bg-card border border-line rounded-[12px] md:h-full md:min-h-0">
      <header className="px-4 py-1 min-h-[52px] border-b border-line flex justify-between items-center gap-2">
        {confirmarVaciar && items.length > 0 ? (
          <>
            <span className="text-[13px] font-semibold text-ink">¿Vaciar la venta ({items.length})?</span>
            <span className="flex flex-none items-center gap-1">
              <button
                onClick={() => {
                  setConfirmarVaciar(false);
                  onLimpiar();
                }}
                className="min-h-[44px] rounded-[9px] px-3 text-[13px] font-semibold text-accent-ink hover:bg-accent-soft"
              >
                Sí, vaciar
              </button>
              <button onClick={() => setConfirmarVaciar(false)} className="min-h-[44px] rounded-[9px] px-3 text-[13px] font-medium text-ink-2 hover:bg-hover-btn">
                No
              </button>
            </span>
          </>
        ) : (
          <>
            <h2 className="font-semibold text-ink">Venta en curso</h2>
            {items.length > 0 && (
              <button onClick={vaciar} className="min-h-[44px] rounded-[9px] px-3 text-[13px] font-medium text-ink-2 hover:bg-hover-btn">
                Vaciar
              </button>
            )}
          </>
        )}
      </header>

      <div className="md:flex-1 md:overflow-y-auto">
        {items.length === 0 ? (
          <div className="p-6 text-center text-ink-3 text-sm">
            <p>Escanea un producto o tócalo del catálogo →</p>
          </div>
        ) : (
          <ul className="divide-y divide-line-row">
            {items.map((it) => (
              <li key={it.producto.presentacion_id} className="p-3">
                <div className="flex justify-between items-start">
                  <div className="min-w-0 pr-2">
                    <p className="font-medium truncate text-ink">{it.producto.nombre}</p>
                    <p className="text-xs text-ink-2 tabular-nums">
                      {it.producto.presentacion_nombre} · {solesDm(it.producto.precio_total_dm)} c/u
                    </p>
                  </div>
                  <button
                    onClick={() => onQuitar(it.producto.presentacion_id)}
                    className="w-11 h-11 flex items-center justify-center rounded-[9px] text-lg leading-none text-ink-3 hover:text-accent-ink hover:bg-hover-btn"
                    aria-label="Quitar del carrito"
                  >
                    ×
                  </button>
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => onSetCantidad(it.producto.presentacion_id, it.cantidad - 1)}
                      className="w-11 h-11 flex items-center justify-center rounded-[9px] text-lg leading-none bg-neutral-soft hover:bg-line text-ink"
                    >
                      −
                    </button>
                    <input
                      type="number"
                      min={1}
                      value={it.cantidad}
                      onChange={(e) => {
                        const n = parseInt(e.target.value, 10);
                        if (!isNaN(n)) onSetCantidad(it.producto.presentacion_id, n);
                      }}
                      className="w-14 h-11 text-center bg-field border border-line-input rounded-[9px] text-ink tabular-nums"
                    />
                    <button
                      onClick={() => onSetCantidad(it.producto.presentacion_id, it.cantidad + 1)}
                      className="w-11 h-11 flex items-center justify-center rounded-[9px] text-lg leading-none bg-neutral-soft hover:bg-line text-ink"
                    >
                      +
                    </button>
                  </div>
                  <p className="font-mono text-sm text-ink tabular-nums">{solesCent(totalLineaCent(it))}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {sugerencia}

      {/* R-C (tramo 03): el total y «Cobrar» siempre a la vista. En el celular este pie es una barra
          fija abajo; desde 768 px es el pie de la columna, que se desplaza por dentro. Es UN solo
          botón en todos los anchos: el e2e busca `^Cobrar · ` y tiene que encontrar uno solo. */}
      <footer className="border-t border-line p-4 space-y-2 max-md:fixed max-md:inset-x-0 max-md:bottom-0 max-md:z-30 max-md:space-y-0 max-md:bg-card max-md:px-3 max-md:pt-2.5 max-md:pb-[max(10px,env(safe-area-inset-bottom))] max-md:shadow-[0_-6px_18px_rgba(36,29,26,0.12)]">
        <div className="flex justify-between text-sm text-ink-2 max-md:hidden">
          <span>Subtotal sin IGV</span>
          <span className="font-mono tabular-nums">{solesCent(totales.subtotal_sin_igv_cent)}</span>
        </div>
        <div className="flex justify-between text-sm text-ink-2 max-md:hidden">
          <span>IGV 18%</span>
          <span className="font-mono tabular-nums">{solesCent(totales.igv_total_cent)}</span>
        </div>
        <div className="flex justify-between text-xl font-bold pt-2 text-ink max-md:hidden">
          <span>Total</span>
          <span className="font-mono tabular-nums">{solesCent(totales.total_cent)}</span>
        </div>

        <div className="flex items-center gap-3 md:block">
          <div className="min-w-0 flex-none md:hidden">
            <p className="text-[11.5px] leading-tight text-ink-2">
              Total · {unidades} {unidades === 1 ? "producto" : "productos"}
            </p>
            <p className="font-mono text-[20px] font-bold leading-tight text-ink tabular-nums">{solesCent(totales.total_cent)}</p>
          </div>
          {/* `bg-ok` y no `bg-ok-strong`: blanco sobre el verde claro da 3,44:1 y el mínimo legible es
              4,5:1 — con el verde del sistema son 5,3:1. Es el botón que más se toca en toda la app y
              se toca con el local lleno; medido sobre lo rendido, no sobre la clase (S17). */}
          <button
            onClick={onCobrar}
            disabled={items.length === 0 || cobrando}
            className="min-h-[48px] flex-1 rounded-[9px] bg-ok px-3 py-3 font-semibold text-white tabular-nums hover:opacity-90 active:opacity-90 disabled:cursor-not-allowed disabled:opacity-30 md:mt-2 md:w-full"
          >
            {cobrando ? "Procesando..." : `Cobrar · ${solesCent(totales.total_cent)}`}
          </button>
        </div>
      </footer>
    </section>
  );
}
